/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC } from 'react';
import { Document } from '@contentful/rich-text-types';
import Link from 'next/link';
import clsx from 'clsx';
import router from 'next/router';

import { CMedia, CType, Media } from '../../models/Contentful/types';
import { IWinnerCardProps, WinnerCategory } from './@types';
import Button from '../Button';
import { IPartnerCardProps } from '../../models/Partner/@types';
import ImageComponent from '../Image';
import { ROUTES } from '../../Constants/variables';
import Typo from '../Typo';
import VideoPlayer from '../VideoPlayer';
import { getChallengeUrl } from '../../models/Challenge';
import { getStrippedString } from '../../helpers';
import helpers from '../../utils/helpers';
import styles from './winnerCard.module.sass';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import DateTimeRenderer from '../DateTime/dateTime';

interface IFirstComponetProps {
  dueDate: string;
  industry: string;
  partners: CType<IPartnerCardProps>[];
  image: CMedia;
  isMobile: boolean;
  category: WinnerCategory;
  media?: string;
  videoUrl?: string;
}

interface ISecondComponent {
  teamName: string;
  title: string;
  description: string | Document;
  duration: string;
  skillNames: string[];
  earnPoints: number;
  isMobile: boolean;
  charactersAllowed: number;
  defaultUrl: string;
  winnerType: string;
  rankIcon?: CType<Media>;
}

const FirstComponent: FC<IFirstComponetProps> = props => {
  const { dueDate, industry, partners, image, isMobile, media, videoUrl } = props;
  return (
    <div className="is-flex is-flex-direction-column ">
      {/* BASIC DETAILS */}
      <div className="is-flex">
        {dueDate && (
          <DateTimeRenderer
            date={dueDate}
            typoProps={{ small: true }}
            typoClasses={clsx('is-color-BlackHalf', styles.dateContainer)}
          />
        )}
        <Typo small classes={clsx('is-color-BlackHalf', styles.industry)}>
          {industry}
        </Typo>
      </div>
      {/* IMAGE AND PARTNERS WITH BORDER */}
      <div className={styles.imageContainer}>
        {media === 'Video' && videoUrl ? (
          <VideoPlayer
            videoUrl={videoUrl}
            classes={styles.videoPlayer}
            videoHeight={isMobile ? '146px' : '100%'}
            aspectRatio={isMobile ? '16/9' : '19/16'}
          />
        ) : (
          <ImageComponent
            image={image}
            width="100%"
            height={isMobile ? '146px' : '195px'}
            aspectRatio="9/16"
            crop
          />
        )}
        <div>
          {partners.slice(0, 3).map((partner, index) => (
            <div key={index} className={styles.sponsorImage}>
              <ImageComponent image={partner?.fields?.image} height={isMobile ? '26px' : '35px'} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SecondComponent: FC<ISecondComponent> = props => {
  const {
    teamName,
    title,
    description,
    skillNames,
    earnPoints,
    duration,
    isMobile,
    defaultUrl,
    winnerType,
    rankIcon,
  } = props;

  const updatedDescription = !isMobile
    ? getStrippedString(description, 92)
    : getStrippedString(description);
  return (
    <div
      className={`${styles.winnerMain} ${
        isMobile && 'is-flex is-align-items-center	is-flex-direction-column'
      }`}
    >
      <div>
        <div className={styles.teamContainer}>
          <ImageComponent image={rankIcon} height={46} width={46} />
          <Typo limitLines={2} classes={styles.teamName}>
            {teamName}
          </Typo>
        </div>
        <Typo h3 limitLines={2} classes={styles.title}>
          {title}
        </Typo>
        {!isMobile && (
          <>
            <Typo normal classes={styles.description}>
              {updatedDescription.string}
              {!!updatedDescription.charLeftCount && updatedDescription.charLeftCount > 0 && (
                <Typo normal>...</Typo>
              )}
            </Typo>
            <Link href={defaultUrl}>
              <Typo normal classes="is-color-Blue">
                Learn more
              </Typo>
            </Link>
          </>
        )}
      </div>
      <div className={styles.extraContainer}>
        {!isMobile && (
          <div
            className={clsx(styles.extraDetails, 'is-flex is-justify-content-space-between mb-3')}
          >
            {winnerType === 'challenge' ? (
              <Typo small>{`${duration} Challenge`}</Typo>
            ) : (
              <Typo small>{`Effort: ${duration}`}</Typo>
            )}
            <Typo small>{`Earns: +${earnPoints}pts`}</Typo>
          </div>
        )}
        {!isMobile && (
          <div className={styles.skillContainer}>
            {skillNames.map((skill, index) => {
              return (
                index < 3 && (
                  <Typo small classes="is-color-BlackHalf is-gap-1" key={index}>
                    {skill}
                  </Typo>
                )
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const WinnerCard: FC<IWinnerCardProps> = props => {
  const { isMobile } = useIsMobileViewport();

  const { teamName, winnerOf, category, submissionUrl, rankIcon, award } = props;
  const { fields } = winnerOf;
  const {
    title,
    description,
    image,
    industryList,
    skills,
    earnPoints,
    // award,
    dueDate,
    duration,
    partners,
    charactersAllowed,
    status,
    project_id,
    slug,
    videoUrl,
    media,
  } = fields;

  let buttonUrl = '';
  let buttonText = 'Do It';
  let defaultUrl = '';
  const winnerType = winnerOf.sys?.contentType?.sys?.id;

  if (winnerType === 'challenge') {
    defaultUrl = getChallengeUrl(fields);
  } else if (winnerType === 'skillbuilder') {
    const linkToUrl = `/skillbuilders/${slug}`;
    defaultUrl = linkToUrl;
  }

  switch (status) {
    case 'active': {
      const linkToUrl = helpers.generatePath(ROUTES.PROJECT, {
        projectId: project_id as string,
      });
      buttonUrl = linkToUrl;
      buttonText = 'View Challenge';
      break;
    }
    case 'completed':
      buttonText = 'Completed';
      break;

    default:
      buttonUrl = defaultUrl;
      break;
  }

  const industry = industryList?.fields?.title?.join(', ');
  const skillNames = skills?.fields?.title ?? [];
  return (
    <div className={`${styles.winnerCard} container`}>
      <FirstComponent
        {...{
          dueDate,
          industry,
          image,
          partners,
          category,
          isMobile,
          media,
          videoUrl,
        }}
      />
      <SecondComponent
        {...{
          title,
          description,
          skillNames,
          earnPoints,
          duration,
          teamName,
          isMobile,
          defaultUrl,
          charactersAllowed,
          winnerType,
          rankIcon,
        }}
      />
      <div className={styles.awardContainer}>
        {isMobile && (
          <div className={styles.skillContainer}>
            {skillNames.slice(0, isMobile ? 1 : 3).map((skill, index) => (
              <Typo small classes="is-color-BlackHalf is-gap-1" key={index}>
                {skill}
              </Typo>
            ))}
          </div>
        )}
        {award &&
          (isMobile ? (
            <Typo normal classes={styles.awardTextMobile}>
              Awarded: {award}
            </Typo>
          ) : (
            <div className="has-text-centered">
              <Typo classes={styles.awardText} normal>
                Awarded:
              </Typo>
              <br />
              <Typo classes={styles.awardText} normal>
                {award}
              </Typo>
            </div>
          ))}
        <div className="is-flex is-flex-direction-column mt-auto">
          <Button
            theme="Filled - Lime"
            onClick={() => buttonUrl && router.push(buttonUrl)}
            disabled={buttonText === 'Completed'}
            classes={styles.button}
          >
            {buttonText}
          </Button>
          {submissionUrl && winnerType === 'challenge' && (
            <Button classes={styles.button} heightLarge onClick={() => router.push(submissionUrl)}>
              View Winning Submission
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WinnerCard;
